import { Box, Button, Typography, Skeleton } from '@mui/material'

import { LoadingButton } from 'components/mui'

import TrayMapSelector from 'components/organisms/TrayMapSelector/TrayMapSelector'
import useTrayContentLogic from './TrayContent.logic'
import { useGrayScaleLogic } from 'components/organisms/GrayScale/GrayScale.logic'
import { OtherTrayItemsProvider } from 'components/organisms/OtherTrayItems'
import { useAddTrayContext } from 'views/TrayManagementLayout/AddTray/AddTray.context'

import './TrayContent.scss'
import classNames from 'classnames'
import { OtherItemsTriggerButton } from 'components/organisms/OtherTrayItems/OtherTrayItemsTriggerButton'
import useDTMS3Image from 'lib/utils/useDTMS3Image'
import { Add } from '@mui/icons-material'
import CustomImplantModal from 'components/organisms/CustomImplantModal/CustomImplantModal'

interface TrayContentProps {
  trayIndex: number
}

const TrayContent: React.FC<TrayContentProps> = ({ trayIndex }) => {
  const { templateData } = useAddTrayContext()
  const {
    subTray,
    saveChanges,
    navigateBack,
    isInstrumentTray,
    traySubTrayTitle,
    otherItems,
    setOtherItems,
    isCustomImplantTray,
    handleCustomImplantModalTrigger,
    isCustomImplantModalOpen,
  } = useTrayContentLogic(trayIndex)

  const { imageUrl: trayImageSrc, isLoading: isTrayImageLoading } =
    useDTMS3Image({
      url: subTray?.trayImg,
      defaultImage: '',
    })

  const {
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
  } = useGrayScaleLogic({
    trayType: subTray.trayType,
    templateData,
  })

  return (
    <OtherTrayItemsProvider items={otherItems} setItems={setOtherItems}>
      <div className="loaner-tray-analysis-content_wrapper">
        <div className="header-section">
          <div className="header-content">
            <Box>
              <Typography variant="h4" lineHeight={1.2} className="main-title">
                {isInstrumentTray
                  ? `View ${traySubTrayTitle}`
                  : `Edit ${traySubTrayTitle} Content`}
              </Typography>
              <Typography
                variant="body1"
                fontSize={14}
                textTransform="capitalize"
                className="tray-info"
              >
                {traySubTrayTitle} Type: <strong>{subTray.trayType}</strong>
              </Typography>
            </Box>
          </div>
        </div>

        <div className="content-section">
          <div className="analysis-panels">
            {subTray.trayImg && (
              <div
                className={classNames('panel image-panel', {
                  'full-width': isInstrumentTray,
                })}
              >
                <div className="panel-header">
                  <Typography variant="h6">Reference Image</Typography>
                </div>
                <div className="panel-content">
                  {isTrayImageLoading ? (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="100%"
                      animation="wave"
                      className="tray-image-skeleton"
                    />
                  ) : (
                    <img
                      src={trayImageSrc}
                      alt="Tray Reference"
                      className="tray-image"
                    />
                  )}
                </div>
              </div>
            )}

            {!isInstrumentTray && (
              <div className="panel map-panel">
                <div className="panel-header tray-map-header">
                  <Typography variant="h6">Digital Tray Map</Typography>
                  <div className="load-all-container">
                    <OtherItemsTriggerButton
                      trayType={subTray?.trayType || 'stryker screw caddy'}
                    />
                    {isCustomImplantTray && (
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleCustomImplantModalTrigger}
                      >
                        Add Custom Products
                      </Button>
                    )}
                    {screwAnnotations.length > 0 && !isCustomImplantTray && (
                      <Button
                        disabled={isAllScrewsLoaded}
                        onClick={loadAllScrews}
                        variant="outlined"
                      >
                        Load All Screws
                      </Button>
                    )}
                    {(plateAnnotations.length > 0 ||
                      drillAnnotations.length > 0) &&
                      !isCustomImplantTray && (
                        <Button
                          disabled={isAllPlatesLoaded}
                          onClick={() => increaseAllPlatesBy(1)}
                          variant="outlined"
                        >
                          Increase All Plates
                        </Button>
                      )}
                  </div>
                </div>
                <div className="panel-content">
                  <TrayMapSelector
                    trayType={subTray.trayType}
                    loadWithUDIs={false}
                    templateData={templateData}
                    trayImg={trayImageSrc}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <CustomImplantModal
          open={isCustomImplantModalOpen}
          onClose={handleCustomImplantModalTrigger}
        />

        <div className="action-buttons">
          <Button
            onClick={navigateBack}
            className={classNames('cancel-button', {
              'confirm-button': isInstrumentTray,
            })}
            variant={!isInstrumentTray ? 'text' : 'contained'}
            color="primary"
          >
            Go Back
          </Button>
          {!isInstrumentTray && (
            <LoadingButton
              onClick={saveChanges}
              className="confirm-button"
              variant="contained"
              color="primary"
              loading={isTrayImageLoading}
              disabled={isTrayImageLoading}
            >
              Save Changes
            </LoadingButton>
          )}
        </div>
      </div>
    </OtherTrayItemsProvider>
  )
}

export default TrayContent
